import axios from "axios";
import TiktokPixel from "tiktok-pixel";
import { v4 as uuid } from "uuid";
import {
  useLocalStorage,
  useSessionStorage,
} from "@/components/detailed-form/utils/hooks";
import LogRocket from "logrocket";
import { LENDER_CATEGORIES } from "../get-started-v3/constants";
import dayjs from "dayjs";
import { handleClaimUpsert as updateClaim } from "@/app/utils/functions";
import { json2csv } from "json-2-csv";
import { message } from "antd";
import { fbPayloadNamesToValues, LOW_VALUE_KEYWORDS } from "../constants";
import { getFailsafeKountId } from "./serverFunctions";
import Clarity from "@microsoft/clarity";

let KountSessionIDFailSafe = null;
let fallBackClaimNumber = null;
let fallBackOmniscore = null;
let fallBackSCOR = null;
let fallBackUTMSource = null;
let ssrCookies = null;

export const getFallbackUTMSource = () => {
  return fallBackUTMSource || "";
};

export const getFallbackKountScore = (type = "OMNISCORE") => {
  switch (type) {
    case "OMNISCORE":
      return fallBackOmniscore || "0";
    case "SCOR":
      return fallBackSCOR || "0";
    default:
      return fallBackOmniscore || "0";
  }
};

export const getFallbackClaimNumber = () => {
  return fallBackClaimNumber;
};

export const setClaimNumberValue = (claimNumberValue = "") => {
  if (claimNumberValue !== "") {
    fallBackClaimNumber = claimNumberValue;
    setCookie("currentId", claimNumberValue, 90);
  }
};

export const LANDING_PAGE_AB_TEST_ENUM = {
  LANDING_PAGE: "landing_page",
  HOME_PAGE: "home_page",
};

export const GET_API_BASE_URL = () => {
  return process.env.NEXT_PUBLIC_BACKEND_API_BASE_URL;
};
export const GET_AI_API_BASE_URL = () => {
  return process.env.NEXT_PUBLIC_AI_API_BASE_URL;
};
export const GET_AI_CALL_CENTER_API_BASE_URL = () => {
  return process.env.NEXT_PUBLIC_AI_CALL_CENTER_API_BASE_URL;
};

export const isProduction = () => {
  const source = window.location.href;
  const isStageOrLocal =
    source?.startsWith("https://www.stage.fair.inc") ||
    source?.startsWith("http://localhost");
  return !isStageOrLocal;
};

const turnOffPixels = true;


export const getUniqueStrings = (array) => {
  return Array.isArray(array) ? Array.from(new Set(array)) : [];
};

let toastFunctions = {
  showToast: () => {},
  showLoading: () => {},
  hideLoading: () => {},
  hideToast: () => {},
};

export const setToastFunctions = (functions) => {
  toastFunctions = functions;
};

export const showToast = (message, severity, options) => {
  toastFunctions.showToast(message, severity, options);
};

export const showLoading = (message, options) => {
  toastFunctions.showLoading(message, options);
};

export const hideToast = () => {
  toastFunctions.hideToast();
};

export const generateUniqueId = () => {
  return Math.floor(10000000 + Math.random() * 90000000).toString();
};

export const getSessionIDorGenerateOne = async () => {
  const sessionID = uuid().replace(/-/g, "");
  KountSessionIDFailSafe = sessionID;
  setCookie("KountSessionID", sessionID);
  return sessionID;
};

export const flattenObj = (obj) => {
  const flattenedObj = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (typeof value === "object") {
      if (key === "basicInfo") {
        flattenedObj[convertKeyToLowerSnakeCase("surname")] = value["surname"];
        flattenedObj[convertKeyToLowerSnakeCase("givenName")] =
          value["givenName"];
        flattenedObj[convertKeyToLowerSnakeCase("yourEmail")] =
          value["yourEmail"];
        flattenedObj[convertKeyToLowerSnakeCase("yourPhone")] =
          value["yourPhone"];
        flattenedObj[convertKeyToLowerSnakeCase("dateOfBirth")] =
          value["dateOfBirth"];
        flattenedObj["address_address"] = value["address"]?.address;
        flattenedObj["address_postcode"] = value["address"]?.postcode;
      } else {
        const innerObjValue = Object.values(value)[0];
        if (typeof innerObjValue === "object") {
          Object.keys(innerObjValue).forEach((innerKey) => {
            flattenedObj[convertKeyToLowerSnakeCase(`${key}_${innerKey}`)] =
              innerObjValue[innerKey];
          });
        } else {
          flattenedObj[convertKeyToLowerSnakeCase(key)] = innerObjValue;
        }
      }
    } else {
      flattenedObj[convertKeyToLowerSnakeCase(key)] = `${value}`;
    }
  });
  return flattenedObj;
};

const convertKeyToLowerSnakeCase = (str) => {
  return str
    .replace(/([A-Z])/g, "_$1")
    .replace(/\./g, "_")
    .toLowerCase();
};

export const findKeyInNestedObject = (object, keyToFind) => {
  let foundObj;
  JSON.stringify(object, (_, nestedValue) => {
    if (nestedValue && nestedValue[keyToFind]) {
      foundObj = nestedValue;
    }
    return nestedValue;
  });
  return foundObj;
};

export const capitalizeString = (string) => {
  if (!string || typeof string !== "string") return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const convertPhoneToUKFormat = (number) => {
  if (!number) return "";
  // Remove leading 0 if any
  let formattedNumber = (number || "").replace(/^0/, "");

  // Add +44 if number is starting with leading 0 instead of +44 or 44 already
  if (!formattedNumber.startsWith("+44") && !formattedNumber.startsWith("44")) {
    formattedNumber = "+44" + formattedNumber;
  }

  return formattedNumber;
};

export const hashString = async (text) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
};

export const handleZohoCreate = async (data) => {
  const source = window.location.href;
  const isStageOrLocal =
    source?.startsWith("https://www.stage.fair.inc") ||
    source?.startsWith("http://localhost");
  if (isStageOrLocal) {
    return null;
  }
  const { loadFromStorage } = useLocalStorage();
  const { loadFromSessionStorage } = useSessionStorage();
  const Email =
    data?.email || loadFromStorage("userAnswers")["basicInfo"]["yourEmail"];
  const claimNumber =
    loadFromStorage("currentId") ||
    loadFromSessionStorage("currentId") ||
    getCookie("currentId") ||
    getFallbackClaimNumber();
  if (!Email.includes("test@fair.inc") && claimNumber) {
    try {
      const response = await axios.post(`${GET_API_BASE_URL()}/lead/`, {
        data: [{ Email, ...data }],
      });
      if (response.status === 500 || response.status === 400) {
        await handleZohoUpsert(data);
      }
    } catch (e) {
      if (e.status === 500 || e.status === 400) {
        await handleZohoUpsert(data);
      } else {
        console.log("ERROR::lead creation", e);
      }
    }
  }
};

export const handleZohoUpsert = async (data, type = "lead", attempt = 0) => {
  if (attempt > 5) {
    return null;
  }
  const source = window.location.href;
  const isStageOrLocal =
    source?.startsWith("https://www.stage.fair.inc") ||
    source?.startsWith("http://localhost");
  if (isStageOrLocal) {
    return null;
  }
  const { loadFromStorage } = useLocalStorage();
  const Email =
    data?.email || loadFromStorage("userAnswers")?.basicInfo?.yourEmail || "";
  if (!Email.includes("test@fair.inc")) {
    try {
      let LEAD_UPSERT_API_PAYLOAD = {};
      Object.keys(data).forEach((key) => {
        if (key !== "Base64_Signature") {
          LEAD_UPSERT_API_PAYLOAD = {
            ...LEAD_UPSERT_API_PAYLOAD,
            [key]: data[key],
          };
        }
      });
      const LEAD_API_URL = `${GET_API_BASE_URL()}/${type}`;
      const response = await axios.patch(LEAD_API_URL, {
        data: [{ Email, ...LEAD_UPSERT_API_PAYLOAD }],
      });
      if (
        response.status === 500 ||
        (response.status === 400 && type === "lead")
      ) {
        await handleZohoUpsert(data, "contact", attempt + 1);
        return;
      }
      if (data.Base64_Signature) {
        const res = await updateClaim({
          ip_address: data?.Signature_IP_Address || "",
        }); // this will update the sign url if signature is present from backend
        const sign_url = res?.success
          ? res?.responseObject?.data?.signature_url
          : undefined;

        if (sign_url) {
          const curRes = await axios.patch(LEAD_API_URL, {
            data: [
              {
                Email,
                Signature_URL: sign_url,
                ...LEAD_UPSERT_API_PAYLOAD,
              },
            ],
          });
          console.log("ZOHO call completed:", curRes);
        } else {
          console.log("Skipping updateClaim as the URL is empty or invalid.");
        }
      }
    } catch (e) {
      console.log("ERROR::lead upsert", e);
      if (type === "lead") {
        await handleZohoUpsert(data, "contact", attempt + 1);
      }
    }
  }
};

export const getLenderMappingByReportNames = async (reportNames) => {
  try {
    if (!reportNames?.length) return null;
    const response = await axios.post(`${GET_API_BASE_URL()}/lenderMap`, {
      report_names: reportNames,
    });
    return response.data?.responseObject;
  } catch (e) {
    console.log("ERROR::get lender mappings", e);
    return null;
  }
};

const sortByStartDate = (dataArray) => {
  return dataArray.sort((a, b) => {
    const dateA = new Date(a.startDate);
    const dateB = new Date(b.startDate);
    // @ts-ignore
    return dateA - dateB; // Ascending order (earliest to latest)
  });
};
const getReportName = (loan) =>
  (
    loan?.supplyCompanyName ||
    loan?.hirePurchase?.supplyCompany ||
    loan?.hirePurchase?.companyName ||
    loan?.defendant
  )?.trim();

export const normalizeAccountNumber = (accountNumber) => {
  return accountNumber
    .replace(/\s+/g, " ") // Collapse multiple spaces to one
    .replace(/^0+/, ""); // Replace trailing zeros with a single zero
};
export const sortAndMapLenders = async (lenders, mapFunction) => {
  // First, apply mapFunction to ensure defendant is properly set
  const mappedLenders = lenders.map(mapFunction).map((loan) => {
    const accountNumber =
      loan?.accountNumber ??
      loan?.hirePurchase?.accountNumber ??
      loan?.rentalAgreement?.accountNumber;

    const normalized_account_number = normalizeAccountNumber(accountNumber);
    if (loan.accountNumber) {
      return {
        ...loan,
        normalized_account_number,
      };
    } else if (loan.hirePurchase?.accountNumber) {
      return {
        ...loan,
        hirePurchase: {
          ...loan.hirePurchase,
          normalized_account_number,
        },
      };
    } else if (loan.rentalAgreement?.accountNumber) {
      return {
        ...loan,
        rentalAgreement: {
          ...loan.rentalAgreement,
          normalized_account_number,
        },
      };
    }
    return loan;
  });

  // Then, sort lenders by start date
  const sortedLenders = sortByStartDate(mappedLenders);

  // Extract report names after sorting
  const reportNames = sortedLenders
    .map((l) => getReportName(l)?.toLowerCase())
    .filter(Boolean);

  // Fetch lender mapping
  const lenderMapping = await getLenderMappingByReportNames(reportNames);

  return sortedLenders.reduce((result, loan) => {
    const reportName = getReportName(loan);
    const mapping = lenderMapping?.[reportName?.toLowerCase()];

    if (
      mapping &&
      (mapping.category === LENDER_CATEGORIES.BLACKLISTED ||
        parseInt(mapping.tier) === 5)
    ) {
      return result;
    }

    if (mapping) {
      loan.universalName = mapping.universal_name;
      loan.tier = parseInt(mapping.tier);
    } else {
      loan.universalName = reportName;
      loan.tier = 0;
      loan.unMappedClaim = true;
    }

    result.push(loan);
    return result;
  }, []);
};

export function base64ToFile(base64String, fileName) {
  const byteCharacters = atob(base64String.split(",")[1]); // Remove metadata
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  return new File([byteArray], fileName, { type: "image/png" });
}

export const trackMixpanel = async (eventName, payload) => {
  const isStageOrLocal =
    window.location.href.startsWith("https://www.stage.fair.inc") ||
    window.location.href.startsWith("http://localhost");
  if (isStageOrLocal) {
    return null;
  }
  const mixpanel = (await import("mixpanel-browser")).default;
  mixpanel.track(eventName, payload);
};

export const SCREENS_EXCLUDED_FOR_CLAIM_PAYLOAD = [
  // standalone pages
  "resume",
  "sign-redo",
  "questions-resume",
  // ancillary pages
  "complaints",
  "cookies-policy",
  "faqs",
  "privacy-policy",
  "terms-and-conditions",
  // and any page that is already added to the screens_completed list (check from backend)
];

export const shouldIncludeScreenName = (screenName) =>
  !SCREENS_EXCLUDED_FOR_CLAIM_PAYLOAD.some((screen) =>
    window.location.href.includes(screen)
  );

export const handleClaimUpsert = async (data, attempt = 0) => {
  if (!isNotDashboard()) return;
  const { loadFromStorage, saveToStorage } = useLocalStorage();
  const { loadFromSessionStorage } = useSessionStorage();
  // console.log("data?.claimNumber", data?.claimNumber);
  // console.log("loadFromStorage('currentId')", loadFromStorage("currentId"));
  const claimNumber =
    data?.claimNumber ||
    loadFromStorage("currentId") ||
    loadFromSessionStorage("currentId") ||
    getCookie("currentId") ||
    getFallbackClaimNumber();
  if (data?.claimNumber) {
    saveToStorage("currentId", data?.claimNumber);
  }
  try {
    const screen_name = `${window.location.hostname}${window.location.pathname}`;
    const referrer = loadFromStorage("referrer");
    const upsertClaimUrl = `${GET_API_BASE_URL()}/session/claim`;
    const upsertClaimUrlStage = "https://stage-v2.fair.inc/claims";
    const sessionStart =
      data?.sessionStart || localStorage.getItem("sessionStart");

    let payload = {
      claim_number: claimNumber,
      ...data,
    };
    if (!screen_name.includes("manual-lender")) {
      payload = {
        ...payload,
        // Adding it after data spread as we want to override the screen_name coming in.
        screen_name: screen_name,
        should_add_screen_name: shouldIncludeScreenName(screen_name),
      };
    }
    if (sessionStart) {
      const sessionEnd = new Date().getTime();
      const duration = sessionEnd - parseInt(sessionStart, 10);
      payload.session_start = sessionStart;
      payload.session_duration = duration;
    }
    const sessionRecordingUrl =
      typeof LogRocket?.sessionURL === "string" &&
      /^https?:\/\/.+/.test(LogRocket.sessionURL)
        ? LogRocket.sessionURL
        : null;
    if (sessionRecordingUrl)
      payload.session_recording_url = sessionRecordingUrl;

    const response = await axios.put(upsertClaimUrl, payload, {
      withCredentials: true,
    });
    // console.log("handleClaimUpsert =>", response.data);
    // triggerDiscordMessage({
    //   content: `# Claim Number: **${claimNumber}**
    //   - In catch block: false
    //   - Response:\n\`\`\`${JSON.stringify(response?.data)}\`\`\``,
    //   bot: DISCORD_BOTS.CLAIM_FAILURE_BOT,
    // });

    const allLoans = aggregateAllLoansInClaim(response?.data?.responseObject);
    localStorage.setItem("current-claim-all-loans", JSON.stringify(allLoans));

    return response.data;
  } catch (e) {
    // Re-instantiate the session using POST session/claim call
    if (e?.response?.status === 401) {
      generateSession("{}")
        .then((res) => {
          if (!res) window.location = "/";
          if (attempt < 11) {
            handleClaimUpsert(data, attempt + 1);
          }
        })
        .catch((err) => {
          console.log("error re-instantiating session", e);
          window.location = "/";
        });
    } else {
      setTimeout(() => {
        if (attempt < 11) {
          handleClaimUpsert(data, attempt + 1);
        }
      }, 30);
    }
    // triggerDiscordMessage({
    //   content: `# Claim Number: **${claimNumber}**
    //   - In catch block: true
    //   - Error:\n\`\`\`${JSON.stringify(e)}\`\`\``,
    //   bot: DISCORD_BOTS.CLAIM_FAILURE_BOT,
    // });
  }
};

export const getLenderName = (loan) => {
  return (
    loan?.supplyCompanyName ||
    loan?.companyName ||
    loan?.lender_name ||
    loan?.defendant ||
    loan?.hirePurchase?.supplyCompanyName ||
    loan?.hirePurchase?.companyName ||
    loan?.hirePurchase?.lender_name ||
    loan?.hirePurchase?.defendant
  )?.trim();
};

export const aggregateAllLoansInClaim = (claim) => {
  const parseHP = (loanObj) =>
    loanObj?.map((shp) => ({
      defendant: getLenderName(shp),
      creditChekType: "experian", // for experian only
      hirePurchase: shp,
      universalName: shp?.universalName,
      tier: shp?.tier,
    }));

  const allLoans = [
    ...(claim?.data?.selected_hire_purchases_for_claim || []),
    ...(claim?.data?.hire_purchases || []),
    ...(claim?.data?.rental_agreements || []),
    ...(parseHP(claim?.data?.experian_hire_purchases) || []),
    ...(parseHP(claim?.data?.experian_other_purchases) || []),
  ];

  const seen = new Set();
  const uniqueLoans = allLoans.filter(
    (loan) =>
      !seen.has(loan?.hirePurchase?.accountNumber) &&
      seen.add(loan?.hirePurchase?.accountNumber)
  );

  return uniqueLoans;
};

export const generateSession = async (utmTags) => {
  if (!isNotDashboard()) return;

  if (window.location.pathname === '/' && window.location.host.includes('mail.fair')) {
    setTimeout(() => {
      window.open('/get-started-v3', '_self');
    }, 500);
  }

  console.log("Generating session with utmTags: ", utmTags);
  const url = `${GET_API_BASE_URL()}/session/claim`;
  const { saveToStorage, loadFromStorage } = useLocalStorage();
  const { saveToSessionStorage, loadFromSessionStorage } = useSessionStorage();

  try {
    const response = await axios.post(url, null, { withCredentials: true });
    const claimId = response?.data?.responseObject?.claimId;
    const session_duration =
      response?.data?.responseObject?.data?.data?.session_duration;
    if (claimId) {
      try {
        const mixpanel = (await import("mixpanel-browser")).default;
        mixpanel.identify(claimId);
      } catch (err) {
        console.error("Error identifying user in mixpanel");
      }
      const utmTagsJSON = JSON.parse(utmTags);
      let channel = "Default";
      const channelCases = {
        Sub2_FairInc: "Sub2",
        outbrain: "Outbrain",
        reclaims: "Reclaims",
        jasonppc: "Jason PPC",
        infinity: "Infinity Leads",
        justizia: "Justizia",
        check_FairInc: "check",
      };
      try {
        Object.keys(utmTagsJSON || {}).forEach((tagKey) => {
          if (tagKey === "utm_campaign") {
            Object.keys(channelCases).forEach((channelCase) => {
              if (utmTagsJSON[tagKey].includes(channelCase)) {
                channel = channelCases[channelCase];
              }
            });
          }
          if (tagKey === "utm_source") {
            const utmSource = utmTagsJSON[tagKey];
            saveToStorage("utm_source", utmSource);
            setCookie("utm_source", utmSource);
            fallBackUTMSource = utmSource;
          }
        });
      } catch (e) {
        console.error("Error setting channel");
      }
      console.log({
        utmTagsJSON,
        channel,
      });
      fallBackClaimNumber = claimId;
      await saveToStorage("currentId", claimId);
      try {
        saveToSessionStorage("currentId", claimId);
      } catch (err) {
        console.error("Error saving session storage", err);
      }
      try {
        setCookie("currentId", claimId, 90);
      } catch (err) {
        console.error("Error saving session storage", err);
      }
      if (session_duration) {
        saveToStorage("sessionStart", Date.now() - session_duration);
      } else {
        saveToStorage("sessionStart", Date.now());
      }
      trackMixpanel("Session generated", {
        "Mongo DB Ref": claimId,
        channel,
        ...JSON.parse(utmTags),
      });
      Clarity.identify(claimId);
      const queryString = new URL(window.location.href).search;
      const params = new URLSearchParams(queryString);
      const fbclid = params.get("fbclid") || getCookie("fbclid") || "";
      const ttclid =
        params.get("ttclid") ||
        getCookie("ttclid") ||
        getCookie("_ttclid") ||
        "";
      const journey = getAndStoreVersion({ skipDBSave: true });
      const startFromFlag = loadFromStorage("START_FROM") || "";
      let initSessionPayload = {
        claimNumber: claimId,
        channel,
        fbclid,
        ttclid,
        journey,
        ...JSON.parse(utmTags),
      };
      if (!startFromFlag && startFromFlag === "") {
        // const abTestValue = await getABTestValue();
        const abTestValue = 1;
        let ab_test_value = "STARTED FROM LANDING_PAGE_V3";
        // let ab_test_value = 'STARTED FROM HOMEPAGE';
        // if (abTestValue === 0) {
        //   console.log(`Redirecting to home page --> ${claimId}`);
        //   saveToStorage('START_FROM', LANDING_PAGE_AB_TEST_ENUM.HOME_PAGE);
        //   ab_test_value = `STARTED FROM HOMEPAGE`;
        // } else {
        //   console.log(`Redirecting to landing page --> ${claimId}`);
        //   saveToStorage('START_FROM', LANDING_PAGE_AB_TEST_ENUM.LANDING_PAGE);
        //   ab_test_value = `STARTED FROM LANDING_PAGE_V3`;
        // }
        initSessionPayload = {
          ...initSessionPayload,
          ab_test_value,
        };
      }
      await handleClaimUpsert(initSessionPayload);
      return true;
    } else {
      console.error("Claim ID is not available in the response");
      return false;
    }
  } catch (error) {
    console.error("Unable to generate session", error);
    return false;
  }
};

export const handleUserUpsert = async (data) => {
  const { loadFromStorage } = useLocalStorage();
  const { loadFromSessionStorage } = useSessionStorage();
  try {
    const upsertUserUrl = `${GET_API_BASE_URL()}/user`;
    const claimNumber =
      loadFromStorage("currentId") ||
      loadFromSessionStorage("currentId") ||
      getCookie("currentId") ||
      getFallbackClaimNumber();
    const payload = { claim_number: claimNumber, ...data };
    const response = await axios.post(upsertUserUrl, payload, {
      withCredentials: true,
    });
    // console.log("handleUserUpsert =>", response.data);
  } catch (e) {
    console.log("Error upserting user");
  }
};

export const OTHER_DISCORD_BOTS = {
  "check.fair.inc": {
    KOUNT_COLLECTED:
      "https://discord.com/api/webhooks/1313080118503280681/HamJiq4lRT1PavIj4YsyXfisuIl74O8X0jZNwg7C8d62-DNnhnfmsHz7OPk8q9HJQSi7",
    LUMINATE_BOT:
      "https://discord.com/api/webhooks/1313083575222145085/ir25bgSn0ODVzHANEj3YlRTmujfztyB28lYRzKCvixHj4Fw26wxXJpKLrpyiy_1cbtQ9",
    CREDIT_CHECK_POST_IDV:
      "https://discord.com/api/webhooks/1313083852285153310/yYcM2seX31BEX0MaPF4-8y-GkBwvYFZ0_Q48cbq6n_3U5Sb4aXTnrifTfH3jNh-T3SLy",
    CREDIT_CHECK:
      "https://discord.com/api/webhooks/1313084166027345920/6BObZ8iyDcUNX5AeyQMIzdb8hmh-osPUpaeFatehb4DA54PwgIO1BuQoKlB9cw-JoUny",
    VALID8_CREDIT_PULL:
      "https://discord.com/api/webhooks/1313084408894459935/hrt_Lc4LWYsjdKS264Qn619x4YDQ3wPoEjcRNj0fk_WlVJOQ-QcnGU94GsqZCtbgbfIs",
    IDV_STARTED:
      "https://discord.com/api/webhooks/1313085060001304646/_vRTxYARzIDoadXn_yjXKVxdC3Jx6Jqadbr5JlHbc3rb6Mx6eL6NKkLvvkZwKLA5uZF1",
    IDV_SELFIE:
      "https://discord.com/api/webhooks/1313085349001433088/tRCmNr7DpYM5GupSp6GTB5YzainfkUvY_fVX9nPYS38AEZz2mIhGl1p8jmGeeoG8YvE6",
  },
  "reclaims.fair.inc": {
    KOUNT_COLLECTED:
      "https://discord.com/api/webhooks/1313085534050193408/8jsLro2t1decnQ9ZM_GyVCkm8aKengQJJVquvoR3cink3WhlrsunR5foEuAIXtnwadlq",
    LUMINATE_BOT:
      "https://discord.com/api/webhooks/1313085624970117120/cBjQcmoMUaa--Eh-vIsltuiaDPkMRK-BxkOUa5RTVgJxshGD9j4zBBtjm0q68GrpCbIv",
    CREDIT_CHECK_POST_IDV:
      "https://discord.com/api/webhooks/1313085733677957120/cHjucWgSbZ0DgvZJHFA0ph3YdpfLUccrFywWn1RjPVcxtVLoYiTAu6fx96fzB1JysyGs",
    CREDIT_CHECK:
      "https://discord.com/api/webhooks/1313085859620192257/IyDtSXwu5pgpyyQcIDkQmdJmBbxW_Ky0fW2f77kzHgsMEuSdxmK-V8R5AMZfCY5Iztdn",
    VALID8_CREDIT_PULL:
      "https://discord.com/api/webhooks/1313086009315037184/pdlucKYDGtgmrj6f4Ipjcc2rg6PK1VSQcXENGlfEdvRy7tdQlcuGxTBfNt0U1Usx1mJj",
    IDV_STARTED:
      "https://discord.com/api/webhooks/1313086135320444958/U1toOKEj4xXcp-WMZcTnGf9Uq6yaLb6tFi6pOaeAumB2g48AfTVLsclroYMakkLXIeJR",
    IDV_SELFIE:
      "https://discord.com/api/webhooks/1313086297992335382/RnbikgqG5nyU4g7lqQoGJkzjHvXI2lhqzOsk5qmmvf__HmuldU4G6hOxLKReNV6T915M",
  },
  "mail.fair.inc": {
    KOUNT_COLLECTED:
      "https://discord.com/api/webhooks/1313086516846657576/_RAzYXGwXstFOJ_k15YBDJ6jj-60-FvU4g4THgRZy_1-awgDapLiHhrDys6AZ0zkE02g",
    LUMINATE_BOT:
      "https://discord.com/api/webhooks/1313086687982915594/78l855smasbcpaCJJH9XRV6CNDd7UM9BeVsZ2HtPYKTH9SlhhtgLfTOFrmKv_XU8N2Wv",
    CREDIT_CHECK_POST_IDV:
      "https://discord.com/api/webhooks/1313087431939063858/6B-x0po-TOR063uFKpnz_upxJCOpvIoxNq1yC47KLnd1iJXvq0pChNlrFbTISyatX4Qa",
    CREDIT_CHECK:
      "https://discord.com/api/webhooks/1313086771390578772/doFm9g4iQpasNr0M5B16scCu8gpT1Bu7Et6MjvUXv5gRv_llTr8xMqux1cloecr9jtTg",
    VALID8_CREDIT_PULL:
      "https://discord.com/api/webhooks/1313086891830153287/KusaGWksK33W55cNpYmZa_iHjJmnYl6mmc0HzRriTtpA1cCzyz3Wp5fcrdatFJbME-Ve",
    IDV_STARTED:
      "https://discord.com/api/webhooks/1313087001095831563/K809LWxLjYnOpCLNUMB1W79tQJk5PojEoGf3kaL_M4zI6GoUrV2Dgc1DbxcIRxXxuLy1",
    IDV_SELFIE:
      "https://discord.com/api/webhooks/1313087140053123104/QyJpHPJeyBBpFbRouglzUipj042fTf8sjI_fNgSYVtv103mUYF_m8a46JvIxE_IUbhXw",
  },
  "connect.fair.inc": {
    KOUNT_COLLECTED:
      "https://discord.com/api/webhooks/1313087255694282822/RGSun12rhSL4ekyclXwJ7Lrp16ALy--xN75MnfAzYuvQO8dAZPmo7PFJzlBW69gw-N8l",
    LUMINATE_BOT:
      "https://discord.com/api/webhooks/1313087334891262024/BKUOU3gr7A-BuM192Oq0UgDBbBjnWALkGvU5g6oVMrV5tckrKnXHfkEC0-dKrWUwtHZY",
    CREDIT_CHECK_POST_IDV:
      "https://discord.com/api/webhooks/1313087538704945152/3nNKICYRUmB-ZjcZUM_MiqGMplKmFh1M2sWM_jwCx8NfDH0mZzm7NpL-zYeRHbBf3O5L",
    CREDIT_CHECK:
      "https://discord.com/api/webhooks/1313087642321027154/b-G7kFdsDp-hvcy0gKsPRNZmKJuAps8SNfN3hvhYZQ8ubmEFj4H07inkOSDeNAQhb4mZ",
    VALID8_CREDIT_PULL:
      "https://discord.com/api/webhooks/1313087736751591515/Nk6j3FGd9NKWvgt2AnIZSUGnRHupXOzbpkwjzyJPCswKT53wSBqeY0H58pKbtsQpnGEa",
    IDV_STARTED:
      "https://discord.com/api/webhooks/1313087829269807125/NRZdSAyVfSey41uv-Dz_adH69UNjSU1b14iKXsfH6jCUJrClUA_IK_7ZNfRVc1N3j5-J",
    IDV_SELFIE:
      "https://discord.com/api/webhooks/1313088026712342539/pWfLViQJ-GVwCzYzUkDJZ_AzwbZKBrtfpkJpemt6gjdd4hBJAuE0WoZvG4VEWpnJ7vUQ",
  },
};

export const DISCORD_BOTS = {
  GET_STATED:
    "https://discord.com/api/webhooks/1293049595986706433/CVrpezfybmpnvFaQbxBmrcB0mzVnEUl5274XrYMIW6MpwSOMEZHitFi-i4zbJ1La_qkp",
  BASIC_INFO:
    "https://discord.com/api/webhooks/1293067094061219910/CHMb6Qt3Y7lqBHUb3BYDVJUYt2re_H054ua9-fvOqDm1XJl2F5OhmrB06IQzWQjP3-2t",
  CONTACT_INFO:
    "https://discord.com/api/webhooks/1293067234612346900/3nqeR5q0rv1rYuJUaH-mGLeXptHbkgO2FhQjtpDHAlOlJAIzbeFPAcMUzueLRhyHWl4l",
  SIGNATURE_COLLECTED:
    "https://discord.com/api/webhooks/1293067342107906118/XbnOvO1lNpp9ZqmnpOAJ-UgNK-IsH6EyanLqiQVwtUYi9QF4M3Lbt_kemXK139twpbH3",
  IDV_SELECTION:
    "https://discord.com/api/webhooks/1293107033897308263/WX_-EaR4I3nW58AzFwbSVgFaIJfE68FKKeA_U4S_XNMdiGhgH4mjSfaW7ezOfZ3oeW0_",
  IDV_OB:
    "https://discord.com/api/webhooks/1293067487268835419/9-of7_GDBtddx7LhV4jiqXAh5s8XQronqFhCxb9x-ZTqgJ_WtdmS9FU7PyYWHSi7hrbr",
  IDV_SELFIE:
    "https://discord.com/api/webhooks/1293067599378255904/dneRdNfBtgroqgCuXsSIWClpRrB1H1a5EhewDxczMsIHpoRqz0jANaiTyyK7XWYh0YV1",
  IDV_STARTED:
    "https://discord.com/api/webhooks/1297863945172025425/CHyvsur460Z7Vs8iPLNrbjbjJrezfWbIdrGWcx97sBIXRL_tTbkvCmCH4oZ6zrJnOKmx",
  KOUNT_COLLECTED:
    "https://discord.com/api/webhooks/1293068101620994121/bvdvok4vPQ92aYbDbdvj4fc-QpXWb9_tNtXSfoXZ0hPg85vvX2RS0jAAeXvn5vTipq7N",
  CREDIT_CHECK:
    "https://discord.com/api/webhooks/1293067805142552668/kjS5CKkmh5SGNul6H4p8sgkEDQmpdOppqdH2jNkcbAkBeufBhFpbwJrVWoQnTvb5feuC",
  CREDIT_CHECK_POST_IDV:
    "https://discord.com/api/webhooks/1308038459474055240/U_SYbu0JLujtwK0sOi1AbG4hJUedYdB5xmCR2btCd1ShosL9a0wbtMuLH3P1tCbvUBMe",
  CLAIM_SUBMITTED:
    "https://discord.com/api/webhooks/1293068013980880916/zF4LuZ-72jlpNC1YrH8MJC6M5bff0JwRUp38cLcyiCwg2JCbIdsuSF55Fe-FXKvS7jj6",
  EMAIL_TRIGGER:
    "https://discord.com/api/webhooks/1293068324963487745/FSrQWAUio-PgKrlnkSO_rwXcCHm2y3395nNNJzmiZiOpgREZkjr4wSa_opF4GFscQJLW",
  SMS_TRIGGER:
    "https://discord.com/api/webhooks/1293068470543716352/nHrPRBR_LvvGbw7R6C-iWNKtW7MKoj58LgUYqDaJYTZo0AYN-x8Vv15h0p6Yrg5leUoq",
  TEST_BOT:
    "https://discord.com/api/webhooks/1293146855945011325/jEeYKlb1I7r1WOOcNZQoyTA8CHVdKJJA9bCb57gPXAM7U_5RtlkS7qdAn6KibaAwcqky",
  BOT_ALERT:
    "https://discord.com/api/webhooks/1293805845301297224/kqOdsTXuqQOZRg7tRO6WX_5qr7AhNAZzlyleaKFvT8mUtTUWa0TFsveFbON7Q6CyI7WS",
  LUMINATE_BOT:
    "https://discord.com/api/webhooks/1294342342932959242/Hu4CNq7ucP8oCDsokM6_Uu-9uO5SVVg_E_RLVVzHEdsjckc29MOsGhCZhF-V3xQpOHRy",
  LUMINATE_QUESTIONS_PULLED_BOT:
    "https://discord.com/api/webhooks/1396963466862137385/zKqr8-WikeBd6IEoFEyRZnF0XAKUH5hACzfs69yBfeVk_r2_pqCCjVLqIwc3epO7Pvq4",
  HIGH_QUALITY_CLAIMS:
    "https://discord.com/api/webhooks/1296434175020109886/9cDTw_7D91VFmYkfkr3ywA9aqiOrEMl7ZfB4nef83ZUOqw7nLzsxAEcdGQy-F4j6gh-E",
  RECLAIMS_HIGH_QUALITY_CLAIMS:
    "https://discord.com/api/webhooks/1303413170890608740/x4IZP7CyxPwr04IWCGiHU19p7yZM7my2WovuxOjKzCpOsJfSAMKgpFfR5eM5QiouVlyp",
  FB_CREDIT_REPORT:
    "https://discord.com/api/webhooks/1296895582756929680/vUMD9CTXF2Me1tmu5G8vBDPGa_8q1T-_c8YRwDwshhmtiw_Gbaa1v8Z1pLxDCrHDq0Dp",
  CLAIM_FAILURE_BOT:
    "https://discord.com/api/webhooks/1297945040009560136/3iW9nKImHxcRHKv9zZ02URj8URFHxKoPihPTbDHMfL5JLNaVSUuTePVL3ZImZ5lqhVMi",
  CREDIT_CHECK_DETAILS:
    "https://discord.com/api/webhooks/1297998022592040970/HodrAwwp2bBfg_90qtxLnVAZ-v5JlAjWJWz8pa4tPXBlUBL0xMUt4hTYcn-7ys2_YmT3",
  HQ_OUTBRAIN_BOT:
    "https://discord.com/api/webhooks/1301209457493147728/oxoRFR57MmSs6veTFIwToCK2jnCLZifr7HBw682i5d239w1W7oN_fhWZbsr41oc1BEDq",
  SECURED_LOANS_BOT:
    "https://discord.com/api/webhooks/1301213695111598170/KN9775hIOkykqR-NSTkJNJQcGRY6OWWCglVncxiomgpapYLnQAylQ4Hv39yrMSIfyeXB",
  UN_SECURED_LOANS_BOT:
    "https://discord.com/api/webhooks/1301214465819279441/2ThRmaUeHMdFU77xLEgI108jHRZX526X4a2pZeY5U1vYeyimjVVlhYs-R10VRGb2nvxH",
  FIXED_TERMS_BOT:
    "https://discord.com/api/webhooks/1301214628637966469/y186QRx5-fQhgGaykzlBMNiaHrqW7p16nr9DUQhq_3Uoyl-X1lC3Hq7KIi0CDpqSq4lu",
  RENTAL_AGREEMENT_BOT:
    "https://discord.com/api/webhooks/1313067344657911899/yS5HMlOLfs_JsVfFe-4J0QE1MVT76DVJ-MwGg1DR54YrwTnt2MZGY-JlP8Y8vFJjIq6j",
  DUPLICATE_LOANS:
    "https://discord.com/api/webhooks/1301500088048160840/O6PsPeVOL1rTUQkRHEFyN9h1NBtI4FfgICRtuVG_h-zLJTUY_tWDaA0iplSVfzMqK9lO",
  RETURNING_USER:
    "https://discord.com/api/webhooks/1302877140919779358/74MBznSqr1-8SrvCe-jbNfMpbk-Et8KmrjpbqAJWi9rmCWSzAaTeYBq5EgvsbZISuOrB",
  DIGITAL_TRUST:
    "https://discord.com/api/webhooks/1308872764396011623/K3aYdyI0z0FX0iUi0W58xLJTrz0Iu1P9iGujrPTZVL5E3fecXEZRVNe-sf4udGGNEWuQ",
  VALID8_CREDIT_PULL:
    "https://discord.com/api/webhooks/1311981962721759286/4lM1xZmmR6qv5l6x5nL4ce9rtpuCi3zndsuvfVSpgmGjwGxh2eI8IKEgRkioVo9fDrnW",
  DUPLICATE_CLAIM:
    "https://discord.com/api/webhooks/1312862184715321496/Sm06z9Ko7Z98w5e_hxxL6iCT6JaQp1tERIA37pIzhICcfGuZjDNVbxzyNdAavpfSZplZ",
  FEEDBACK:
    "https://discord.com/api/webhooks/1314230820290170890/j2DEwj6e92nuBDCyz37vYv78IEXWCTh82TEsYogO0mz7ACbp66RxPhxmZM21A6kMKuHH",
  MANUALLY_SELECTED_LENDERS:
    "https://discord.com/api/webhooks/1313870485666140251/jeP2nMxQpBOS4UV1tAwi-JQj0s7aDevAVZVS2AgJkOqOLRE-ehWc_a7RkCisRxNF4mlE",
  ONFIDO_EVENTS:
    "https://discord.com/api/webhooks/1321581628983742484/1jDUMyRUL6DTfWypRJQ55HDqh_wU2VQqz_ZUDFmCgnVrFKHRq6VUAilNRRRZYYZdSFFy",
  UNSIGNED_BOTS:
    "https://discord.com/api/webhooks/1348330364845686945/IGsP0O56SV4CUI7gcDEVoqCD8w-F_UdN5fZu0KbMUYvPJQhP_gVo68fdVd4ihgMEok_k",
};

export const SUB2_EVENTS = {
  // [DISCORD_BOTS.GET_STATED]: 'dealer_comm_disclosed',
  POSTCODE: "postcode",
  Lead: "lead_contact",
  // lead_with_credit: 'lead_with_credit',
  lead_with_credit: "Lead",
  CompleteRegistration: "CompleteRegistration",
  lead_with_credit_new_pixel: "Lead_NEW_PIXEL",
  car_financed_0721: "car_financed_0721",
  [DISCORD_BOTS.BASIC_INFO]: "basic_info",
  [DISCORD_BOTS.CONTACT_INFO]: "contact_info",
  signed_up: "signed_up",
  claims_found: "claims_found",
  DISCLAIMER: "disclaimer",
  [DISCORD_BOTS.SIGNATURE_COLLECTED]: "sign_claim",
  CREDIT_RE_CHECK: "credit_check_failed",
  credit_report: "credit_report",
  LENDER: "lender",
  SELFIE_UPLOAD: "id_selfie_upload",
  // [DISCORD_BOTS.LUMINATE_BOT]: 'luminate',
  luminate: "luminate",
  dealer_comm_disclosed: "dealer_comm_disclosed",
  // LEAD_WITH_IDV: 'lead_with_idv',
  LEAD_WITH_IDV: "Contact",
  HIGH_VALUE_LEAD: "HighValueLead",
  IDV_COMPLETED: "IDVCompleted",
};

export const DATALAYER_EVENTS = {
  Lead: "lead_with_credit",
  Lead_NEW_PIXEL: "lead_with_credit_new_pixel",
  Contact: "lead_with_idv",
  IDVCompleted: "lead_with_idv_complete",
};

export const TiktokPixel_EVENTS = {
  postcode: "Search",
  car_financed_0721: "ViewContent",
  basic_info: "basic_info",
  sign_claim: "Subscribe",
  lead_without_idv: "SubmitForm",
  Lead: "CompleteRegistration",
  Contact: "Contact",
  contact_info: "contact_info",
  disclaimer: "disclaimer",
  credit_check_failed: "credit_check_failed",
  credit_report: "credit_report",
  lender: "lender",
  id_selfie_upload: "id_selfie_upload",
  luminate: "luminate",
  dealer_comm_disclosed: "dealer_comm_disclosed",
};

export const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const isSSR = () => typeof window === 'undefined';

export const getCookie = (name) => {
  if (isSSR()) {
    return ssrCookies[name] || "";
  } else {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  }
};

export const setCookie = (name, value, days = 10) => {
  // let expires = "";
  // if (days) {
  //   const date = new Date();
  //   date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  //   expires = `; expires=${date.toUTCString()}`;
  // }
  // document.cookie = `${name}=${value || ""}${expires}; path=/`;
  if (isSSR()) {
    // Server-side rendering
    const cookieOptions = {
      name,
      value,
      maxAge: days * 24 * 60 * 60, // maxAge in seconds for server-side
      path: '/',
      httpOnly: true, // Recommended for security
      secure: process.env.NODE_ENV === 'production', // Recommended for production
      // Add other options as needed, e.g., sameSite
    };
    // cookies().set(cookieOptions);
    ssrCookies = {
      ...ssrCookies,
      [name]: value
    }
  } else {
    // Client-side rendering
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${value || ""}${expires}; path=/`;
    ssrCookies = {
      ...ssrCookies,
      [name]: value
    }
  }
};

const getIP = (callback) => {
  fetch("https://api.ipify.org?format=json")
    .then((response) => response.json())
    .then((data) => callback(data.ip))
    .catch((error) => callback(null, error));
};

const OUTBRAIN_EVENTS = {
  [SUB2_EVENTS.lead_with_credit]: SUB2_EVENTS.lead_with_credit,
  [SUB2_EVENTS.Lead]: SUB2_EVENTS.Lead,
};

const sendOutbrainPostback = (clickId, eventName) => {
  if (OUTBRAIN_EVENTS[eventName]) {
    const postbackUrl = `https://tr.outbrain.com/unifiedPixel?ob_click_id=${clickId}&name=${eventName}`;
    const sendPostback = async () => {
      try {
        const response = await fetch(postbackUrl, {
          method: "GET",
        });

        if (!response.ok) {
          console.error("Postback request failed", response);
        }
      } catch (error) {
        console.error("Error sending postback:", error);
      }
    };

    sendPostback();
  }
};

const MAX_RETRIES = 3; // Maximum number of retries
const RETRY_DELAY = 1000; // Delay between retries in milliseconds

async function sendFacebookPixelEventWithRetries(
  pixelId,
  claimNumber,
  accessToken,
  eventId,
  ip,
  hashedEmail,
  hashedPhone,
  fbc,
  fbp,
  event_key
) {
  const eventData = {
    access_token: accessToken,
    data: [
      {
        event_name: eventId,
        event_id: event_key,
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: `${window.location.hostname}${window.location.pathname}`,
        action_source: "website",
        user_data: {
          client_ip_address: `${ip}`,
          client_user_agent: navigator.userAgent,
          em: hashedEmail,
          ph: hashedPhone,
          fbc: fbc,
          fbp: fbp,
        },
        custom_data: {
          claim_id: claimNumber,
        },
        opt_out: false,
      },
    ],
  };

  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await axios.post(
        `https://graph.facebook.com/v21.0/${pixelId}/events`,
        eventData,
        config
      );
      return response.data; // Return the response if successful
    } catch (error) {
      console.error(
        `Attempt ${attempt + 1} failed for event ${eventId}:`,
        error
      );

      // Send error message to Discord
      try {
        await triggerDiscordMessage({
          content: `Error in triggering sub2 event: ${eventId}\n
                    '''${JSON.stringify(error)?.substring?.(0, 2001)}'''`,
          bot: DISCORD_BOTS.TEST_BOT,
        });
      } catch (err) {
        console.error(
          "Failed to send error message to Discord during facebook pixel event sending:",
          err
        );
      }

      // If this was the last attempt, log the error and break the loop
      if (attempt === MAX_RETRIES - 1) {
        console.error(
          `All ${MAX_RETRIES} attempts failed for event: ${eventId}`
        );
        throw new Error(
          `All ${MAX_RETRIES} attempts failed for event: ${eventId}`
        ); // Throw an error
      }

      // Wait before retrying
      console.log(
        `Waiting before next attempt to hit facebook pixel event: ${eventId}`
      );
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
    }
  }
}

async function sendGTMServerEventWithRetries(
  claimNumber,
  accessToken,
  eventId,
  ip,
  hashedEmail,
  hashedPhone,
  fbc,
  fbp,
  event_key,
  fname,
  lname,
  ttclid,
  ttp,
  leadSource,
  leadPayload = {}
) {
  const eventData = {
    event_name: eventId,
    event: eventId,
    event_id: event_key.replace(/[-_]/g, ""),
    event_time: Math.floor(Date.now() / 1000),
    host: leadSource,
    // event_source_url: `${window.location.hostname}${window.location.pathname}`,
    // action_source: "website",
    user_data: {
      client_ip_address: `${ip}`,
      client_user_agent: navigator.userAgent,
      em: hashedEmail,
      ph: hashedPhone,
      fbc: fbc,
      fbp: fbp,
      ttclid,
      ttp,
    },
    context: {
      user: {
        ip: `${ip}`,
        user_agent: navigator.userAgent,
        email: hashedEmail,
        phone_number: hashedPhone,
        fbc: fbc,
        fbp: fbp,
        ttclid,
        ttp,
        external_id: event_key.replace(/[-_]/g, ""),
      },
      ad: {
        callback: ttclid,
      },
    },
    ...leadPayload,
  };

  const config = {
    // headers: {}
  };

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await axios.post(
        `${GET_API_BASE_URL()}/trigger-gtm-event`,
        {
          data: eventData,
        },
        config
      );
      return response.data; // Return the response if successful
    } catch (error) {
      console.error(
        `Attempt ${attempt + 1} failed for event ${eventId}:`,
        error
      );

      // Send error message to Discord
      try {
        await triggerDiscordMessage({
          content: `Error in triggering sub2 event: ${eventId}\n
                  '''${JSON.stringify(error)?.substring?.(0, 2001)}'''`,
          bot: DISCORD_BOTS.TEST_BOT,
        });
      } catch (err) {
        console.error(
          "Failed to send error message to Discord during facebook pixel event sending:",
          err
        );
      }

      // If this was the last attempt, log the error and break the loop
      if (attempt === MAX_RETRIES - 1) {
        console.error(
          `All ${MAX_RETRIES} attempts failed for event: ${eventId}`
        );
        throw new Error(
          `All ${MAX_RETRIES} attempts failed for event: ${eventId}`
        ); // Throw an error
      }

      // Wait before retrying
      console.log(
        `Waiting before next attempt to hit facebook pixel event: ${eventId}`
      );
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
    }
  }
}

const getCurrentTimestampUTCFormatted = () => {
  const now = new Date();

  // Get year, month, and day in UTC
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, "0"); // Month is 0-indexed
  const day = String(now.getUTCDate()).padStart(2, "0");

  // Get hours, minutes, seconds, and milliseconds in UTC
  const hours = String(now.getUTCHours()).padStart(2, "0");
  const minutes = String(now.getUTCMinutes()).padStart(2, "0");
  const seconds = String(now.getUTCSeconds()).padStart(2, "0");
  const milliseconds = String(now.getUTCMilliseconds()).padStart(3, "0");

  // UTC offset is always +00:00
  const timezoneOffset = "+00:00";

  // Construct the formatted timestamp string
  const formattedTimestamp = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${timezoneOffset}`;

  return formattedTimestamp;
};

export const getLandingPage = () => {
  const landingPages = {
    CHECK: "check.fair.inc/",
    MAILONLINE: "mailonline.fair.inc/",
    DIRECT: "direct.fair.inc/",
    CLAIM: "claim.fair.inc/",
    SUBMIT: "submit.fair.inc/",
  };
  const source = window.location.href;
  if (source?.startsWith?.("https://check.fair.inc")) return landingPages.CHECK;
  else if (source?.startsWith?.("https://mailonline.fair.inc"))
    return landingPages.MAILONLINE;
  else if (source?.startsWith?.("https://direct.fair.inc"))
    return landingPages.DIRECT;
  else if (source?.startsWith?.("https://claim.fair.inc"))
    return landingPages.CLAIM;
  else if (source?.startsWith?.("https://submit.fair.inc"))
    return landingPages.SUBMIT;
  else if (source?.startsWith?.("http://localhost")) return landingPages.DIRECT;
  else return "";
};

export const triggerDigiReachEvent = () => {
  const source = window.location.href;
  const transactionId = localStorage.getItem("transaction_id") || "";
  const isStageOrLocal =
    source?.startsWith("https://www.stage.fair.inc") ||
    source?.startsWith("http://localhost");
  if (transactionId === "") {
    console.log("No transactionId found");
    return null;
  }
  if (isStageOrLocal) {
    console.log(`STAGE: Tried to trigger digi reach event: ${transactionId}`);
    return null;
  }
  const isNotDigiReach = !source?.startsWith("https://digital.fair.inc");
  if (isNotDigiReach) {
    console.log("Not a DigiReach page, not sending postback");
    return null;
  }
  axios.post(`${GET_API_BASE_URL()}/digi-reach/postback`, {
    transactionId: transactionId,
  });
};

export const triggerSub2DataLayer = (eventId, eventPayload = {}) => {
  if (
    [SUB2_EVENTS.lead_with_credit, SUB2_EVENTS.LEAD_WITH_IDV].includes(eventId)
  ) {
    try {
      localStorage.setItem(
        `lead_event_${eventId}`,
        `${parseInt(localStorage.getItem(`lead_event_${eventId}`) || "0") + 1}`
      );
    } catch (e) {}
  }
  const source = window.location.href;
  const isStageOrLocal =
    source?.startsWith("https://www.stage.fair.inc") ||
    source?.startsWith("http://localhost");
  if (isStageOrLocal) {
    return null;
  }

  let pixelId, accessToken, leadSource;
  if (source?.startsWith?.("https://reclaims.fair.inc")) {
    pixelId = "1163293635411298";
    accessToken =
      "EAAI9ZBIWYfpIBO3vSGJR2DH9hTS9IXZByqCtywuzxxZAwZAN3On7OSEMGe6Y4YraeBLaz4WGeKvbdZCGRGs36CsDIKVKKbQSTOEPB63a3KVERx66qAKsEa9j8gd6cdLwYZCEZAgntNBYXmnnjnFt2W5fUFZB3irDFOwjTClOAqR7x5pzdg26IuSJ1vhiKbV6xEDQDAZDZD";
    leadSource = "https://reclaims.fair.inc";
    // pixelId = "1725241211571906";
    // accessToken = "EAAI9ZBIWYfpIBO3vSGJR2DH9hTS9IXZByqCtywuzxxZAwZAN3On7OSEMGe6Y4YraeBLaz4WGeKvbdZCGRGs36CsDIKVKKbQSTOEPB63a3KVERx66qAKsEa9j8gd6cdLwYZCEZAgntNBYXmnnjnFt2W5fUFZB3irDFOwjTClOAqR7x5pzdg26IuSJ1vhiKbV6xEDQDAZDZD";
  } else if (source?.startsWith?.("https://check.fair.inc")) {
    // pixelId = "1320204099178577";
    // // accessToken = "EAAI9ZBIWYfpIBO3vSGJR2DH9hTS9IXZByqCtywuzxxZAwZAN3On7OSEMGe6Y4YraeBLaz4WGeKvbdZCGRGs36CsDIKVKKbQSTOEPB63a3KVERx66qAKsEa9j8gd6cdLwYZCEZAgntNBYXmnnjnFt2W5fUFZB3irDFOwjTClOAqR7x5pzdg26IuSJ1vhiKbV6xEDQDAZDZD";
    // accessToken = "EAAI9ZBIWYfpIBO8x8SiT21W0d4U2ZAjO81M8OLR0DynMRCsNGwoZAqmzlLuZCHZCzJzbuiCdua7DPe6INTi20BMoGZCrHx9f1BkWYQhqwEjF6dxu1j7OeYxAI0FvlDF5dgSE6DNs0vOABIUbFieJgNoP4bOlkpD4zphSMmGZBQe1PZAu7KQ5egPMcNrPdIylag4ZCAgZDZD";
    leadSource = "https://check.fair.inc";
    pixelId = "446869674890562";
    accessToken =
      "EAAM3QEHpOtsBOyOW0X0vOUg9N8ZASlB2uE5jtkJpCQjdnxZBlTikcaXkB8LMDJsFyH564XCkZCsLtNwz4UA94DE425ZCff7PbzYMlSllQtK0piFZBPtxUNlmsDBNP4wkTvfwiWunhf7rvZBiWjpWVEAnAvRUOOwy7o3kfMF20LDBw0Uw2kELweWtbd8XkS6UdznQZDZD";
  } else if (source?.startsWith?.("https://new.fair.inc")) {
    // pixelId = "1320204099178577";
    // // accessToken = "EAAI9ZBIWYfpIBO3vSGJR2DH9hTS9IXZByqCtywuzxxZAwZAN3On7OSEMGe6Y4YraeBLaz4WGeKvbdZCGRGs36CsDIKVKKbQSTOEPB63a3KVERx66qAKsEa9j8gd6cdLwYZCEZAgntNBYXmnnjnFt2W5fUFZB3irDFOwjTClOAqR7x5pzdg26IuSJ1vhiKbV6xEDQDAZDZD";
    // accessToken = "EAAI9ZBIWYfpIBO8x8SiT21W0d4U2ZAjO81M8OLR0DynMRCsNGwoZAqmzlLuZCHZCzJzbuiCdua7DPe6INTi20BMoGZCrHx9f1BkWYQhqwEjF6dxu1j7OeYxAI0FvlDF5dgSE6DNs0vOABIUbFieJgNoP4bOlkpD4zphSMmGZBQe1PZAu7KQ5egPMcNrPdIylag4ZCAgZDZD";
    leadSource = "https://check.fair.inc";
    pixelId = "446869674890562";
    accessToken =
      "EAAM3QEHpOtsBOyOW0X0vOUg9N8ZASlB2uE5jtkJpCQjdnxZBlTikcaXkB8LMDJsFyH564XCkZCsLtNwz4UA94DE425ZCff7PbzYMlSllQtK0piFZBPtxUNlmsDBNP4wkTvfwiWunhf7rvZBiWjpWVEAnAvRUOOwy7o3kfMF20LDBw0Uw2kELweWtbd8XkS6UdznQZDZD";
  } else if (source?.startsWith?.("https://find.fair.inc")) {
    pixelId = "1306369483984425";
    accessToken =
      "EAAI9ZBIWYfpIBO3vSGJR2DH9hTS9IXZByqCtywuzxxZAwZAN3On7OSEMGe6Y4YraeBLaz4WGeKvbdZCGRGs36CsDIKVKKbQSTOEPB63a3KVERx66qAKsEa9j8gd6cdLwYZCEZAgntNBYXmnnjnFt2W5fUFZB3irDFOwjTClOAqR7x5pzdg26IuSJ1vhiKbV6xEDQDAZDZD";
    leadSource = "https://find.fair.inc";
  } else if (source?.startsWith?.("https://mail.fair.inc")) {
    pixelId = "982782316511194";
    accessToken =
      "EAAI9ZBIWYfpIBO3vSGJR2DH9hTS9IXZByqCtywuzxxZAwZAN3On7OSEMGe6Y4YraeBLaz4WGeKvbdZCGRGs36CsDIKVKKbQSTOEPB63a3KVERx66qAKsEa9j8gd6cdLwYZCEZAgntNBYXmnnjnFt2W5fUFZB3irDFOwjTClOAqR7x5pzdg26IuSJ1vhiKbV6xEDQDAZDZD";
    leadSource = "https://mail.fair.inc";
  } else if (source?.startsWith?.("https://connect.fair.inc")) {
    pixelId = "1635083703770345";
    accessToken =
      "EAAI9ZBIWYfpIBO3vSGJR2DH9hTS9IXZByqCtywuzxxZAwZAN3On7OSEMGe6Y4YraeBLaz4WGeKvbdZCGRGs36CsDIKVKKbQSTOEPB63a3KVERx66qAKsEa9j8gd6cdLwYZCEZAgntNBYXmnnjnFt2W5fUFZB3irDFOwjTClOAqR7x5pzdg26IuSJ1vhiKbV6xEDQDAZDZD";
    leadSource = "https://connect.fair.inc";
  } else if (source?.startsWith?.("https://claim.fair.inc")) {
    // pixelId = "1789163888603129";
    pixelId = "1019267763731123";
    accessToken =
      "EAAI9ZBIWYfpIBO3vSGJR2DH9hTS9IXZByqCtywuzxxZAwZAN3On7OSEMGe6Y4YraeBLaz4WGeKvbdZCGRGs36CsDIKVKKbQSTOEPB63a3KVERx66qAKsEa9j8gd6cdLwYZCEZAgntNBYXmnnjnFt2W5fUFZB3irDFOwjTClOAqR7x5pzdg26IuSJ1vhiKbV6xEDQDAZDZD";
    leadSource = "https://claim.fair.inc";
  } else if (source?.startsWith?.("https://submit.fair.inc")) {
    pixelId = "1019267763731123";
    accessToken =
      "EAAI9ZBIWYfpIBO3vSGJR2DH9hTS9IXZByqCtywuzxxZAwZAN3On7OSEMGe6Y4YraeBLaz4WGeKvbdZCGRGs36CsDIKVKKbQSTOEPB63a3KVERx66qAKsEa9j8gd6cdLwYZCEZAgntNBYXmnnjnFt2W5fUFZB3irDFOwjTClOAqR7x5pzdg26IuSJ1vhiKbV6xEDQDAZDZD";
    leadSource = "https://submit.fair.inc";
  } else if (source?.startsWith?.("https://direct.fair.inc")) {
    pixelId = "1287845198939073";
    accessToken =
      "EAAI9ZBIWYfpIBOySW3RQNmkG962jCTjFkbU55mYQ9V880KiPWNCsXyVdUlkn4FEltEBsbEWz07IjOypFXifqmW70pN96mhJ52thKL6ZBMrKOmwaeZAdg1xIPRZA4TAkr2cjxGPp2F0y5FZA7dgZBPVpxWJgpVYRQyNCVGdXRJ9KGZBo3upeIMjXbz1WwUXs5YERZBQZDZD";
    leadSource = "https://direct.fair.inc";
  } else {
    // pixelId = "448467197650935"
    // accessToken = "EAANcNeEdddsBOzrF2zPWbeqnZBzIZBKQ6Qog1mZBNjjSoBfKrv2CawcokcpQXo1jKrpqwZBnDZA12QsKkotweXWnxYzvbEltR9xN81K89YkCj41otc8iD1ZCLvoh0XZA5NuvomsEZBlLtTIbxAliMpnH4o6FsZApnWBMXH06UK57fK6jE2U253ZAqG6eJeeCkRSidpZAwZDZD"

    pixelId = "619874984328933";
    accessToken =
      "EAAI9ZBIWYfpIBO9YmloT8ybKQPsBuOVsglsWB4MLNQQmFz36DSqJUZB6ILpH4aOvxfondkpJ5IPPa5NmKjkpHQ4wtP9ZBrKqt4eZAcouk04n4XioOJ95MaxcBTulNWNZAZAfZA0NMFtZAS0cSlVq7ZBZCp25adpwrUaIzsk3b2bOZCTI8HersIIccboHoD6K9Fw1RhtIAZDZD";
    leadSource = "https://www.fair.inc";
  }

  const { loadFromStorage } = useLocalStorage();
  const claimNumber = loadFromStorage("currentId") || getCookie('currentId') || getFallbackClaimNumber();
  const userAnswers = loadFromStorage("userAnswers");
  const fbp = getCookie("_fbp");
  const fbc = getCookie("_fbc");
  const ttp = getCookie("_ttp");
  const ttclid = getCookie("ttclid") || getCookie("_ttclid");
  const dicbo_id_encoded = getCookie("dicbo_id") || "{}";
  const dicbo_id_decoded = dicbo_id_encoded
    ? decodeURIComponent(dicbo_id_encoded)
    : "{}";
  const dicbo_id_json = dicbo_id_decoded ? JSON.parse(dicbo_id_decoded) : {};
  const dicbo_id = dicbo_id_json?.dicbo_fetch || "";

  // Generate event_key so that same can be used for data layer and CAPI deduplication
  // const event_key = `${claimNumber}_${DATALAYER_EVENTS[eventId] || eventId}`;
  // const event_key = `${claimNumber}_${TiktokPixel_EVENTS[eventId] || eventId}_${generateUUID()}`;
  const event_key = `${claimNumber}_${TiktokPixel_EVENTS[eventId] || eventId}`;
  let hashed_E = userAnswers?.basicInfo?.["yourEmail"] || "";
  let hashed_P = userAnswers?.basicInfo?.["yourPhone"] || "";
  hashString(userAnswers?.basicInfo?.["yourEmail"]).then((em) => {
    hashed_E = em;
  });
  hashString(userAnswers?.basicInfo?.["yourPhone"]).then((ph) => {
    hashed_P = ph;
  });
  // @ts-ignore
  window.dataLayer = window.dataLayer || [];
  let leadPayload = {};

  switch (eventId) {
    case SUB2_EVENTS.lead_with_credit:
      leadPayload = {
        "tier_1/2": "yes",
        pre_2021: "yes",
        signup_status: "yes",
        ...eventPayload,
      };
      break;
    case SUB2_EVENTS.credit_report:
      leadPayload = {
        "tier_1/2": "no",
        pre_2021: "yes",
        signup_status: "no",
        ...eventPayload,
      };
      break;
    default:
      leadPayload = { ...eventPayload };
      break;
  }
  // @ts-ignore
  dataLayer.push({
    event: DATALAYER_EVENTS[eventId] || eventId,
    claim_id: `${event_key}`, // `${claimNumber}`,
    event_id: `${event_key}`, // `${claimNumber}`,
    email: userAnswers?.basicInfo?.["yourEmail"] || "",
    phone: userAnswers?.basicInfo?.["yourPhone"] || "",
    ...leadPayload,
  });
  getIP((ip, error) => {
    if (error) {
      console.error("Error getting IP address:", error);
    } else {
      let hashedEmail = userAnswers?.basicInfo?.["yourEmail"] || "";
      let hashedPhone = userAnswers?.basicInfo?.["yourPhone"] || "";
      let fname = userAnswers?.basicInfo?.["givenName"] || "";
      let lname = userAnswers?.basicInfo?.["surname"] || "";
      hashString(userAnswers?.basicInfo?.["yourEmail"]).then((em) => {
        hashedEmail = em;
      });
      hashString(userAnswers?.basicInfo?.["yourPhone"]).then((ph) => {
        hashedPhone = ph;
      });
      setTimeout(() => {
        // ================== NEW CODE FOR FACEBOOK PIXEL EVENT FIRING START ==================
        if (leadSource !== "https://check.fair.inc" && false) {
          !turnOffPixels &&
            sendFacebookPixelEventWithRetries(
              pixelId,
              claimNumber,
              accessToken,
              eventId,
              ip,
              hashedEmail,
              hashedPhone,
              fbc,
              fbp,
              event_key
            )
              .then((data) => {
                console.log(
                  `Facebook Pixel event sent successfully: ${eventId}`,
                  data
                );
              })
              .catch((err) => {
                console.error("Facebook Pixel event failed:", err?.message);
              });

          try {
            // @ts-ignore
            if (!turnOffPixels && fbq) {
              // @ts-ignore
              fbq("track", eventId);
              console.log("Track fbq ::", eventId);
            }
          } catch (e) {
            console.log("ERROR calling fbq ::", { e });
          }
        }
        // ================== NEW CODE FOR FACEBOOK PIXEL EVENT FIRING END ==================

        // ================== NEW CODE FOR GTM SERVER EVENT FIRING START ==================
        // alert(leadSource);
        if (claimNumber && claimNumber !== "") {
          sendGTMServerEventWithRetries(
            claimNumber,
            accessToken,
            ttclid ? TiktokPixel_EVENTS[eventId] || eventId : eventId,
            ip,
            hashedEmail,
            hashedPhone,
            fbc,
            fbp,
            event_key,
            fname,
            lname,
            ttclid,
            ttp,
            leadSource,
            leadPayload
          )
            .then((data) => {
              console.log(
                `GTM server event sent successfully: ${eventId}`,
                data
              );
            })
            .catch((err) => {
              console.error("GTM server event failed:", err?.message);
            });
        }
        // ================== NEW CODE FOR GTM SERVER EVENT FIRING END ==================

        sendOutbrainPostback(`${dicbo_id || generateUUID()}`, eventId);
        const tikTokPixelPayload = {
          event: TiktokPixel_EVENTS[eventId] || eventId,
          event_time: Math.floor(Date.now() / 1000),
          // "event_id": `${generateUUID()}`,
          // "event_id": `${claimNumber}_${TiktokPixel_EVENTS[eventId] || eventId}`,
          event_id: event_key, // `${claimNumber}`,
          user: {
            phone: hashedPhone,
            email: hashedEmail,
            ip: `${ip}`,
            user_agent: `${navigator.userAgent} || ''`,
            ttp,
            ttclid,
          },
          properties: {
            claim_id: `${event_key}`,
            lead_source: leadSource,
          },
        };
        !turnOffPixels &&
          console.log("TT Event :: ", {
            tikTokPixelPayload,
          });
        !turnOffPixels &&
          axios
            .post(`${GET_API_BASE_URL()}/tiktok-event`, tikTokPixelPayload)
            .catch((err) => {
              console.error("ERROR while triggering server side event", err);
            });
        !turnOffPixels &&
          TiktokPixel.track(TiktokPixel_EVENTS[eventId] || eventId, {
            ...tikTokPixelPayload,
          });
      }, 200);
    }
  });
};

const getBotByHostName = (bot) => {
  const host = window.location.hostname;
  let targetKey = "";
  let tempBot = bot;
  let OtherBots = {};
  if (OTHER_DISCORD_BOTS[host]) {
    OtherBots = OTHER_DISCORD_BOTS[host];
  }
  if (Object.keys(OtherBots).length > 0) {
    OtherBots.forEach((Botkey) => {
      if (bot === DISCORD_BOTS[Botkey]) {
        targetKey = Botkey;
      }
    });
    if (targetKey !== "") {
      if (OtherBots[targetKey]) {
        tempBot = OtherBots[targetKey];
      }
    }
  }
  return tempBot;
};

const getBotKey = (bot) => {
  let tempKey = "TEST_BOT";
  Object.keys(DISCORD_BOTS).forEach((botKey) => {
    if (DISCORD_BOTS[botKey] === bot) {
      tempKey = botKey;
    }
  });

  return tempKey;
};

export const triggerDiscordMessage = ({
  content = "",
  bot,
  eventPayload = {},
}) => {
  const { loadFromStorage } = useLocalStorage();
  const referrer = loadFromStorage("referrer");
  const OMNISCORE = loadFromStorage("OMNISCORE") || "0";
  const SCOR = loadFromStorage("SCOR") || "0";
  const targetBotURL =
    window.location.href.includes("localhost") ||
    window.location.href.includes("stage")
      ? DISCORD_BOTS.TEST_BOT
      : bot; //getBotByHostName(bot);
  fetch(targetBotURL, {
    body: JSON.stringify({
      content: `${
        LogRocket?.sessionURL
          ? `
        ${content}\n[Watch LogRocket Session Click Here](${LogRocket?.sessionURL})`
          : `${content}`
      }
        Source: \`${window.location.href}\`
        ${referrer ? `Referrer: \`${referrer}\`` : ""}
        Journey: **${retrieveVersion()}**
        Consolidated Rating: **${OMNISCORE}**
        Footprint Score: **${SCOR}**`,
    }),
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  })
    .then(function (res) {
      // console.log(res);
    })
    .catch(function (res) {
      // console.log(res);
    });

  if (SUB2_EVENTS[bot]) {
    triggerSub2DataLayer(SUB2_EVENTS[bot], eventPayload);
  }

  // 🟢 Send to Slack via your backend

  const mrkdwnContent = content
    .replace(/# Claim Number: /g, ">")
    .replace(/\*\*/gm, "*")
    .replace(/^\s*-\s/gm, "    • ");
  const fullMessage = `${
    LogRocket?.sessionURL
      ? `${mrkdwnContent}\n<${LogRocket.sessionURL}|Watch LogRocket Session Click Here>`
      : `${mrkdwnContent}`
  }\n*Source:* \`${window.location.href}\`\n${
    referrer ? `*Referrer:* \`${referrer}\`` : ""
  }\n*Journey:* *${retrieveVersion()}*\n*Consolidated Rating:* *${OMNISCORE}*\n*Footprint Score:* *${SCOR}*`;
  const notificationsUrl = `${GET_API_BASE_URL()}/notifications`;
  fetch(notificationsUrl, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      channel: getBotKey(bot),
      text: fullMessage,
    }),
  })
    .then((res) => res.json())
    .then((res) => {
      // console.log("Notification sent:", res);
    })
    .catch((err) => {
      console.error("Error sending Notification:", err);
    });
};

export const processKountRiskInquiry = async () => {
  const { loadFromStorage, saveToStorage } = useLocalStorage();
  const { loadFromSessionStorage } = useSessionStorage();
  const claimNumber =
    loadFromStorage("currentId") ||
    loadFromSessionStorage("currentId") ||
    getCookie("currentId") ||
    getFallbackClaimNumber();
  const userAnswers = loadFromStorage("userAnswers");
  const kountSessionId =
    getCookie("KountSessionID") ||
    loadFromSessionStorage("KountSessionID") ||
    loadFromStorage("KountSessionID");
  const payload = {
    session_id:
      kountSessionId || getFailsafeKountId() || KountSessionIDFailSafe || uuid().replace(/-/g, ""),
    email: userAnswers?.basicInfo?.["yourEmail"],
    phone: userAnswers?.basicInfo?.["yourPhone"],
    claim_number: claimNumber,
    name: `${userAnswers?.basicInfo?.["givenName"] || ""} ${
      userAnswers?.basicInfo?.["surname"] || ""
    }`,
    address: userAnswers?.basicInfo?.["address"]?.address,
    city: userAnswers?.basicInfo?.["address"]?.postTown,
    postalCode: userAnswers?.basicInfo?.["address"]?.postcode,
    country: userAnswers?.basicInfo?.["address"]?.country,
  };

  const url = `${GET_API_BASE_URL()}/kount`;
  axios
    .post(url, payload, { withCredentials: true })
    .then((kountResponse) => {
      const {
        IP_COUNTRY = "UNKNOWN",
        OMNISCORE = 0,
        SCOR = 0,
        IP_IPAD = ``,
        FINGERPRINT = ``,
        COOKIES = ``,
        JAVASCRIPT = ``,
        SESS = ``,
        MODE = ``,
      } = kountResponse.data?.responseObject || {};
      saveToStorage("risk-assessment", kountResponse.data);
      saveToStorage("OMNISCORE", OMNISCORE);
      saveToStorage("SCOR", SCOR);
      fallBackOmniscore = OMNISCORE;
      fallBackSCOR = SCOR;
      /**
       * triggering after contact page. Pls trigger then and add persona,
       * omniscore, geo  and lead source (fb/tiktok/display/direct) to the BOTs
       *   */
      const info = userAnswers?.basicInfo;
      triggerDiscordMessage({
        content: `# Claim Number: **${claimNumber}**
        - Entry Point: **${window.location.href}**
        - Name: **${info?.givenName} ${info?.surname}**
        - DOB: **${info?.dateOfBirth}**
        - Address: **${JSON.stringify(info?.address?.address)}**
        - Country(Kount): **${IP_COUNTRY}**
        - SessionID: **${kountSessionId}**
        - IP_IPAD: **${IP_IPAD}**
        - FINGERPRINT: **${FINGERPRINT}**
        - COOKIES: **${COOKIES}**
        - JAVASCRIPT: **${JAVASCRIPT}**
        - SESS: **${SESS}**
        - MODE: **${MODE}**`,
        bot: DISCORD_BOTS.KOUNT_COLLECTED,
      });
    })
    .catch((e) => {
      console.error("ERROR processing Kount", {
        e,
      });
    });
};

export const extractUtmParams = (url) => {
  const utmParams = {};

  const allowedKeysOrPrefixes = [
    "utm_",
    "source",
    "fbclid",
    "transaction_id",
    "publisher_id",
    "sub_id",
  ];

  const extractFromUrl = (targetUrl) => {
    try {
      const parsedUrl = new URL(targetUrl);
      const params = new URLSearchParams(parsedUrl.search);

      // @ts-ignore
      for (const entry of params.entries()) {
        const [key, value] = entry;
        if (
          allowedKeysOrPrefixes.some(
            (prefix) => key === prefix || key.startsWith(prefix)
          )
        ) {
          utmParams[key] = value;
          try {
            localStorage.setItem(key, value);
          } catch (e) {
            console.warn(`Could not store ${key}`, e);
          }
        }
      }
    } catch (error) {
      console.error("Invalid URL during UTM extraction", { targetUrl, error });
    }
  };

  // Step 1: Try current URL
  extractFromUrl(url || window.location.href);

  const hasUTMParams = Object.keys(utmParams).some((key) =>
    key.startsWith("utm_")
  );

  if (!hasUTMParams && document.referrer) {
    extractFromUrl(document.referrer);
  }

  return JSON.stringify(utmParams);
};

export const getCurrentUTCDateTime = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = (now.getUTCMonth() + 1).toString().padStart(2, "0"); // Month is 0-indexed
  const day = now.getUTCDate().toString().padStart(2, "0");
  const hours = now.getUTCHours().toString().padStart(2, "0");
  const minutes = now.getUTCMinutes().toString().padStart(2, "0");
  const seconds = now.getUTCSeconds().toString().padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
};

export const getAndStoreVersion = (skipDBSave = false) => {
  const { saveToStorage } = useLocalStorage();
  try {
    const url = new URL(window.location.href);
    const version = url.searchParams.get("source");

    if (version) {
      // Only store if the parameter is present
      saveToStorage("version", version);
      !skipDBSave &&
        handleClaimUpsert({
          journey: version,
        });
      return version; // Return the version if available
      // return 'c'; // Return the version if available
    } else {
      // let defaultValue = 'default';
      let defaultValue = "c";
      const host = window.location.href.toLowerCase();
      const hostMappings = {
        "mail.fair.inc": "c",
        "www.fair.inc": "c",
        "mailonline.fair.inc": "c",
        "check.fair.inc": "c",
        "digital.fair.inc": "c",
      };
      defaultValue = Object.keys(hostMappings).find((key) => host.includes(key))
        ? hostMappings[
            Object.keys(hostMappings).find((key) => host.includes(key))
          ]
        : "c";

      saveToStorage("version", defaultValue);
      !skipDBSave &&
        handleClaimUpsert({
          journey: defaultValue,
        });
      return defaultValue; // Return null if not found
      // return 'c'; // Return the version if available
    }
  } catch (error) {
    console.error("Error parsing URL or accessing localStorage:", error);
    // let defaultValue = 'default';
    let defaultValue = "c";
    const host = window.location.href.toLowerCase();
    const hostMappings = {
      "mail.fair.inc": "c",
      "www.fair.inc": "c",
      "mailonline.fair.inc": "c",
      "check.fair.inc": "c",
      "digital.fair.inc": "c",
    };
    defaultValue = Object.keys(hostMappings).find((key) => host.includes(key))
      ? hostMappings[
          Object.keys(hostMappings).find((key) => host.includes(key))
        ]
      : // : 'default';
        "c";
    saveToStorage("version", defaultValue);
    !skipDBSave &&
      handleClaimUpsert({
        journey: defaultValue,
      });
    return defaultValue; // Return null on error
    // return 'c'; // Return null on error
  }
};

export const retrieveVersion = () => {
  const { loadFromStorage } = useLocalStorage();
  const storedVersion = loadFromStorage("version") || "c"; //'default';
  return storedVersion; // Return the stored version or null if not found
  // return 'c'; // Return the stored version or null if not found
};

export const formatAgreementDate = (
  dateString // July 12, 2022
) => dayjs(dateString).format("MMMM D, YYYY");

export const formatAgreementDate1 = (
  dateString // July 12, 2022
) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
export const metadataMap = {
  "/": {
    title: "Fair.inc | Claim Mis-Sold PCP & HP Car Finance Refunds",
    description:
      "Recover £1000s from mis-sold PCP and HP car finance with Fair.inc. Quick 60-second check, no cost, no hassle. Trusted experts since 2008.",
    viewport: "width=device-width, initial-scale=1, maximum-scale=1",
    openGraph: {
      url: "https://www.fair.inc/",
      title: "Fair.inc | Claim Mis-Sold PCP & HP Car Finance Refunds",
      description:
        "Recover £1000s from mis-sold PCP and HP car finance with Fair.inc. Quick 60-second check, no cost, no hassle. Trusted experts since 2008.",
      type: "website",
    },
  },
  "/claim-assistance": {
    title: "Car Finance Claim Assistance - Mis-Sold PCP & HP Claims | Fair Inc",
    description:
      "Get expert help to claim compensation for mis-sold PCP & HP car finance. Quick eligibility check, no cost, FCA regulated. Trusted experts since 2008.",
    viewport: "width=device-width, initial-scale=1, maximum-scale=1",
    openGraph: {
      url: "https://www.fair.inc/claim-assistance",
      title:
        "Car Finance Claim Assistance - Mis-Sold PCP & HP Claims | Fair Inc",
      description:
        "Get expert help to claim compensation for mis-sold PCP & HP car finance. Quick eligibility check, no cost, FCA regulated. Trusted experts since 2008.",
      type: "website",
    },
  },
  "/faqs": {
    title: "FAQs - Car Finance Claims: PCP & HP Mis-sold Contracts | Fair Inc",
    description:
      "Find answers on PCP/HP mis-sold claims, eligibility, compensation, and more. Check if you're eligible for a refund now.",
    viewport: "width=device-width, initial-scale=1, maximum-scale=1",
    openGraph: {
      url: "https://www.fair.inc/faqs",
      title:
        "FAQs - Car Finance Claims: PCP & HP Mis-sold Contracts | Fair Inc",
      description:
        "Find answers on PCP/HP mis-sold claims, eligibility, compensation, and more. Check if you're eligible for a refund now.",
      type: "website",
    },
  },
  "/complaints": {
    title: "File a Complaint | Fair Inc",
    description:
      "Submit complaints via phone, email, or mail. We aim to resolve issues promptly and fairly.",
    viewport: "width=device-width, initial-scale=1, maximum-scale=1",
    openGraph: {
      url: "https://www.fair.inc/complaints",
      title: "File a Complaint | Fair Inc",
      description:
        "Submit complaints via phone, email, or mail. We aim to resolve issues promptly and fairly.",
      type: "website",
    },
  },
  "/terms-and-conditions": {
    title: "Terms & Conditions | Fair Inc",
    description:
      "Review our Terms and Conditions for Fair Claims Management services, including fees, rights, and responsibilities.",
    viewport: "width=device-width, initial-scale=1, maximum-scale=1",
    openGraph: {
      url: "https://www.fair.inc/terms-and-conditions",
      title: "Terms & Conditions | Fair Inc",
      description:
        "Review our Terms and Conditions for Fair Claims Management services, including fees, rights, and responsibilities.",
      type: "website",
    },
  },
  "/my-claims": {
    title: "Claim Status & Information | Fair Inc",
    description:
      "Check the status of your claim and find important information about your Letter of Authority.",
    viewport: "width=device-width, initial-scale=1, maximum-scale=1",
    openGraph: {
      url: "https://www.fair.inc/my-claims",
      title: "Claim Status & Information | Fair Inc",
      description:
        "Check the status of your claim and find important information about your Letter of Authority.",
      type: "website",
    },
  },
  "/privacy-policy": {
    title: "Privacy Policy | Fair Inc",
    description:
      "Discover how we collect, use, and protect your personal data at Fair, in compliance with UK GDPR and data protection laws.",
    viewport: "width=device-width, initial-scale=1, maximum-scale=1",
    openGraph: {
      url: "https://www.fair.inc/privacy-policy",
      title: "Privacy Policy | Fair Inc",
      description:
        "Discover how we collect, use, and protect your personal data at Fair, in compliance with UK GDPR and data protection laws.",
      type: "website",
    },
  },
};

export const hasNameTitle = (name) => {
  if (!name) return false;
  const NAME_TITLES = [
    "Mr",
    "Mrs",
    "Ms",
    "Miss",
    "Mx",
    "Dr",
    "Prof",
    "Eng",
    "Engr",
    "Arch",
    "Gen",
    "Col",
    "Maj",
    "Capt",
    "Lt",
    "Sgt",
    "Rev",
    "Fr",
    "Pr",
    "Imam",
    "Rabbi",
    "Hon",
    "Sen",
    "Gov",
    "Amb",
    "MP",
  ];
  return NAME_TITLES.some((title) => {
    const regex = new RegExp(`^${title}\\.?\\b`, "i");
    return regex.test(name);
  });
};

export const getSingleAddressLine = (addressObject) => {
  const {
    poBox,
    subBuilding,
    name,
    number,
    street1,
    street2,
    district,
    postTown,
    postcode,
    county,
    country,
  } = addressObject;
  const p_address = [];
  if (poBox) p_address.push(poBox);
  if (subBuilding) p_address.push(subBuilding);
  if (name) p_address.push(name);
  if (number) p_address.push(number);
  if (street1) p_address.push(street1);
  if (street2) p_address.push(street2);
  if (district) p_address.push(district);
  if (postTown) p_address.push(postTown);
  if (postcode) p_address.push(postcode);
  if (county) p_address.push(county);
  if (country) p_address.push(country);
  const addressLabel = p_address.join(", ");
  return addressLabel;
};

export const isValidName = (name) => {
  if (!name) return false; // Handle null or undefined names.
  const trimmedName = name.trim(); // Trim whitespace.
  return (
    /^[A-Za-z]+(?:[ ][A-Za-z]+)*$/.test(trimmedName) &&
    trimmedName.length >= 3 &&
    trimmedName.length <= 50 &&
    !/\d/.test(trimmedName)
  ); // Ensure no numbers are present.
};
export function searchTextFromList(searchText, arr, options = {}) {
  // Default options
  const {
    caseSensitive = false,
    exactMatch = false,
    partialWord = true,
    returnScores = false,
  } = options;

  // If empty search text, return empty array
  if (!searchText.trim()) {
    return [];
  }

  // Prepare search text
  const processedSearchText = caseSensitive
    ? searchText
    : searchText.toLowerCase();
  const searchTerms = processedSearchText
    .split(/\s+/)
    .filter((term) => term.length > 0);

  // Score each item in the array
  const scoredResults = arr.map((item, index) => {
    let score = 0;
    let matches = 0;
    const processedItem = caseSensitive ? item : item.toLowerCase();

    // Check each search term against the item
    for (const term of searchTerms) {
      if (exactMatch) {
        // Exact match scoring
        if (processedItem === term) {
          score += 100;
          matches++;
        }
      } else if (partialWord) {
        // Partial word matching
        const index = processedItem.indexOf(term);
        if (index !== -1) {
          // Higher score for matches at beginning of word/string
          const positionBonus = index === 0 ? 10 : 0;
          // Higher score for longer matches
          const lengthBonus = (term.length / searchText.length) * 20;
          score += 50 + positionBonus + lengthBonus;
          matches++;
        }
      } else {
        // Whole word matching
        const regex = new RegExp(`\\b${term}\\b`, caseSensitive ? "" : "i");
        if (regex.test(item)) {
          score += 75;
          matches++;
        }
      }
    }

    // Bonus if all terms match
    if (matches === searchTerms.length && matches > 0) {
      score += 25;
    }

    // Bonus for exact substring match (not at word boundary)
    if (!exactMatch && processedItem.includes(processedSearchText)) {
      score += 15;
    }

    return { item, index, score };
  });

  // Filter out non-matches and sort by score
  const filtered = scoredResults.filter((result) => result.score > 0);
  filtered.sort((a, b) => b.score - a.score);

  // Return based on options
  if (returnScores) {
    return filtered;
  } else {
    return filtered.map((result) => ({
      item: result.item,
      index: result.index,
    }));
  }
}

export const exportToCSV = (jsonArr, sheetTitle, columnsList = null) => {
  try {
    let filteredData = jsonArr;

    if (
      Array.isArray(columnsList) &&
      columnsList.every((col) => typeof col === "string")
    ) {
      // Pick only specified keys in the desired order
      filteredData = jsonArr.map((item) => {
        const row = {};
        columnsList.forEach((col) => {
          row[col] = item[col] !== undefined ? item[col] : "";
        });
        return row;
      });
    }

    const csvData = json2csv(filteredData, {
      emptyFieldValue: "",
    });

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${sheetTitle?.replace(" ", "_")}_${dayjs().format(
      "YYYY-MM-DD"
    )}.csv`;
    link.click();

    message.info("CSV File Downloaded");
  } catch (error) {
    console.log("export to csv failed", error);
    message.error("Export to CSV Failed");
  }
};

export const getABTestValue = async () => {
  // Math.random() generates a number between 0 (inclusive) and 1 (exclusive).
  // We check if the number is less than 0.5.
  // If it is, we return 0; otherwise, we return 1.
  // This gives us an approximately 50% chance of getting either 0 or 1.
  return (await Math.random()) < 0.5 ? 0 : 1;
};

export const get10PercentABTestValue = async () => {
  // Math.random() generates a number between 0 (inclusive) and 1 (exclusive).
  // To redirect 10% of traffic, we check if the number is less than 0.1.
  // If it is, we return 1 (representing the new screen); otherwise, we return 0 (representing the original screen).
  // This gives us an approximately 10% chance of getting 1 and 90% chance of getting 0.
  return (await Math.random()) < 0.1 ? 1 : 0;
};

export const getOS = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/android/i.test(userAgent)) {
    return "Android";
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return "iOS";
  }
  if (/windows phone/i.test(userAgent)) {
    return "Windows Phone"; // Less common, but good to include
  }
  if (/windows/i.test(userAgent)) {
    return "Windows";
  }
  if (/mac/i.test(userAgent)) {
    return "Mac";
  }
  if (/linux/i.test(userAgent)) {
    return "Linux";
  }
  return "Unknown"; // Desktop defaults here if no specific OS matched
};

export const getCurrentDatetimeUtcFormatted = (date) => {
  const now = date ? new Date(date) : new Date();
  const utcYear = now.getUTCFullYear();
  const utcMonth = (now.getUTCMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed
  const utcDay = now.getUTCDate().toString().padStart(2, "0");
  const utcHours = now.getUTCHours().toString().padStart(2, "0");
  const utcMinutes = now.getUTCMinutes().toString().padStart(2, "0");
  const utcSeconds = now.getUTCSeconds().toString().padStart(2, "0");

  return `${utcYear}-${utcMonth}-${utcDay}T${utcHours}:${utcMinutes}:${utcSeconds}`;
};

/**
 * Background job runner
 */
const delay = (ms) => new Promise((res) => setTimeout(res, ms));

// const safeWriteFile = (filePath, data) => {
//   const tempPath = `${filePath}.tmp`;
//   fs.writeFileSync(tempPath, JSON.stringify(data, null, 2));
//   fs.renameSync(tempPath, filePath);
// };

export const performZohoUpsert = async (data, type = "lead", attempt = 0) => {
  if (attempt > 5) throw new Error("Max retry attempts reached");

  const Email = data?.email || data?.Email;
  if (!Email.includes("test@fair.inc")) {
    try {
      let LEAD_UPSERT_API_PAYLOAD = {};
      Object.keys(data).forEach((key) => {
        if (key !== "Base64_Signature") {
          LEAD_UPSERT_API_PAYLOAD = {
            ...LEAD_UPSERT_API_PAYLOAD,
            [key]: data[key],
          };
        }
      });
      const LEAD_API_URL = `${GET_API_BASE_URL()}/${type}`;
      const response = await axios.patch(LEAD_API_URL, {
        data: [{ Email, ...LEAD_UPSERT_API_PAYLOAD }],
      });
      if (
        response.status === 500 ||
        (response.status === 400 && type === "lead")
      ) {
        return await performZohoUpsert(data, "contact", attempt + 1);
      }
      if (data.Base64_Signature) {
        const res = await updateClaim({
          ip_address: data?.Signature_IP_Address || "",
        }); // this will update the sign url if signature is present from backend
        const sign_url = res?.success
          ? res?.responseObject?.data?.signature_url
          : undefined;

        if (sign_url) {
          const curRes = await axios.patch(LEAD_API_URL, {
            data: [
              {
                Email,
                Signature_URL: sign_url,
                ...LEAD_UPSERT_API_PAYLOAD,
              },
            ],
          });
          console.log("ZOHO call completed:", curRes);
        } else {
          console.log("Skipping updateClaim as the URL is empty or invalid.");
        }
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        console.log("Axios error:", {
          message: e.message,
          status: e.response?.status,
          data: e.response?.data,
          error: JSON.stringify(e.response?.data?.responseObject),
          config: e.config,
        });
      } else {
        console.log("ERROR::lead upsert", e);
      }
      if (type === "lead") {
        return await performZohoUpsert(data, "contact", attempt + 1);
      } else {
        throw e;
      }
    }
  }
};

export const sendNotification = async ({ bot, content }) => {
  try {
    const notificationsUrl = `${GET_API_BASE_URL()}/notifications`;

    const response = await fetch(notificationsUrl, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        channel: getBotKey(bot),
        text: content,
      }),
    });

    const result = await response.json();
    return result;
  } catch (err) {
    console.error("Error sending notification:", err);
    return null;
  }
};
export const isPre2021 = (dateString) => {
  // Create a Date object from the input string.
  // The Date constructor can parse 'YYYY-MM-DD' strings directly.
  const date = new Date(dateString);

  // Get the full year from the Date object.
  const year = date.getFullYear();

  // Compare the year to 2021.
  return year < 2021;
};

export const getFBPayloadValue = (lenderName) => {
  const lowerCasedLenderName = lenderName?.toLowerCase();
  const leadValue = fbPayloadNamesToValues[lowerCasedLenderName] || 75;
  return leadValue;
};

export const getLoanPercentage = (loan) => {
  //  (loan end date - start date) in months / no of periods loan %
  const { startDate, endDate, fixedPaymentTerms = {} } = loan;
  const { numberOfPayments = 0 } = fixedPaymentTerms;
  const periods = numberOfPayments;
  if (!endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const monthsDifference =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());
  if (monthsDifference <= 0 || periods <= 0) return 0; // Avoid division by zero
  const percentage = (monthsDifference / periods) * 100;
  const result = Math.round(percentage * 100) / 100; // Round to two decimal
  return result > 100 ? 100 : result; // Ensure it doesn't exceed 100%
};

export const groupStringsByFirstLetter = (strings) => {
  // Sort the strings array in an case-insensitive manner
  const sortedStrings = strings.sort((a, b) => {
    const upperA = a.toUpperCase();
    const upperB = b.toUpperCase();
    if (upperA < upperB) return -1;
    if (upperA > upperB) return 1;
    return 0;
  });

  // Group the strings by their first letter
  const groupedStrings = sortedStrings.reduce((acc, curr) => {
    const firstLetter = curr[0].toUpperCase();
    const existingGroup = acc[firstLetter];
    if (existingGroup) {
      existingGroup.push(curr);
    } else {
      acc[firstLetter] = [curr];
    }
    return acc;
  }, {});

  // Sort the groups by their first letter
  const sortedGroups = Object.keys(groupedStrings)
    .sort()
    .reduce((acc, curr) => {
      acc[curr] = groupedStrings[curr];
      return acc;
    }, {});

  return sortedGroups;
};

export const isNotDashboard = () => {
  const pathname = window.location.href;
  return !pathname?.includes("d-leads");
};

export const isValueableLead = (names = []) => {
  const namesArray = [...names];
  // return namesArray.some((name) => {
  //   const nameLower = name.toLowerCase();

  //   // ...that does NOT contain ANY of the keywords.
  //   // The .every() method checks if the condition is true for all keywords.
  //   // The condition here is that the name does NOT include the keyword.
  //   const hasNoKeywords = LOW_VALUE_KEYWORDS.every(
  //     (keyword) => !nameLower.includes(keyword)
  //   );

  //   return hasNoKeywords;
  // });

  let tempNames = [];
  names.forEach(name => {
    const lowerCasedName = name.toLowerCase();
    let isFound = false;
    LOW_VALUE_KEYWORDS.forEach(keyword => {
      if (lowerCasedName.includes(keyword)) {
        isFound = true;
      }
    });
    if (!isFound) {
      tempNames.push(lowerCasedName);
    }
  });
  return tempNames.length > 0;
};

export const notBlacklistedRoute = (pathname) =>
  !pathname?.includes("id-selfie-upload") &&
  !pathname?.includes("id-selfie-upload-gbg") &&
  !pathname?.includes("id-selfie-upload-persona") &&
  !pathname?.includes("resume") &&
  !pathname?.includes("test-zoho") &&
  !pathname?.includes("sftp-insights") &&
  !pathname?.includes("sign-redo") &&
  !pathname?.includes("status") &&
  !pathname?.includes("no-win-no-fee") &&
  !pathname?.includes("manual-lender") &&
  !pathname?.includes("call-center") &&
  !pathname?.includes("localhost");
